import api from './api';
import { unwrapApiResponse } from '@/utils/api-response.util';
import { ActivityReport, ReportOptions, SelfActivityReport, AiReportListItem, AiReportDetail } from '@/types/client-reports';

// Експертні ендпоінти (Legacy GET - для сумісності)
export const getActivityReport = async (
  clientId: string,
  from: string,
  to: string,
): Promise<ActivityReport> => {
  const response = await api.get(`/client-reports/${clientId}/activity`, {
    params: { from, to },
  });
  return unwrapApiResponse<ActivityReport>(response.data);
};

export const sendActivityReportByEmail = async (
  clientId: string,
  from: string,
  to: string,
): Promise<void> => {
  await api.get(`/client-reports/${clientId}/activity/send-email`, {
    params: { from, to },
  });
};

export const sendActivityReportByTelegram = async (
  clientId: string,
  from: string,
  to: string,
): Promise<{ success: boolean; message: string }> => {
  const response = await api.get(`/client-reports/${clientId}/activity/send-telegram`, {
    params: { from, to },
  });
  return response.data;
};

// Експертні ендпоінти (Нові консистентні POST + Options)
export const getClientReportOptions = async (clientId: string): Promise<ReportOptions> => {
  const response = await api.get(`/client/${clientId}/reports/options`);
  return unwrapApiResponse<ReportOptions>(response.data);
};

export const getClientActivityReport = async (
  clientId: string,
  from: string,
  to: string,
  sections?: string[],
  eventTypeIds?: number[],
): Promise<SelfActivityReport> => {
  const response = await api.post(`/client/${clientId}/reports/activity`, {
    from,
    to,
    ...(sections && sections.length > 0 ? { sections } : {}),
    ...(eventTypeIds && eventTypeIds.length > 0 ? { eventTypeIds: eventTypeIds.join(',') } : {}),
  });
  const { meta, data } = response.data;
  return { ...meta, ...data } as SelfActivityReport;
};

export const downloadClientActivityReportPdf = async (
  clientId: string,
  from: string,
  to: string,
  sections?: string[],
  eventTypeIds?: number[],
): Promise<void> => {
  const response = await api.post(
    `/client/${clientId}/reports/activity/pdf`,
    {
      from,
      to,
      ...(sections && sections.length > 0 ? { sections } : {}),
      ...(eventTypeIds && eventTypeIds.length > 0 ? { eventTypeIds: eventTypeIds.join(',') } : {}),
    },
    { responseType: 'blob' },
  );
  const url = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = `report_${clientId}_${from}_${to}.pdf`;
  a.click();
  URL.revokeObjectURL(url);
};

export const sendClientActivityReportByEmail = async (
  clientId: string,
  from: string,
  to: string,
  sections?: string[],
  eventTypeIds?: number[],
): Promise<void> => {
  await api.post(`/client/${clientId}/reports/activity/send-email`, {
    from,
    to,
    ...(sections && sections.length > 0 ? { sections } : {}),
    ...(eventTypeIds && eventTypeIds.length > 0 ? { eventTypeIds: eventTypeIds.join(',') } : {}),
  });
};

export const sendClientActivityReportByTelegram = async (
  clientId: string,
  from: string,
  to: string,
  sections?: string[],
  eventTypeIds?: number[],
): Promise<{ success: boolean; message: string }> => {
  const response = await api.post(`/client/${clientId}/reports/activity/send-telegram`, {
    from,
    to,
    ...(sections && sections.length > 0 ? { sections } : {}),
    ...(eventTypeIds && eventTypeIds.length > 0 ? { eventTypeIds: eventTypeIds.join(',') } : {}),
  });
  return response.data;
};

export const getClientActivityAiContext = async (
  clientId: string,
  from: string,
  to: string,
  sections?: string[],
): Promise<string> => {
  const response = await api.post(`/client/${clientId}/reports/activity/ai-context`, {
    from,
    to,
    ...(sections && sections.length > 0 ? { sections } : {}),
  });
  return unwrapApiResponse<{ markdown: string }>(response.data).markdown;
};

export interface AiAnalysisResult {
  html: string;
  fromCache: boolean;
}

export interface ClientAiReportListItem extends AiReportListItem {
  initiatedBy: 'client' | 'expert';
}

export interface ClientAiReportDetail extends AiReportDetail {
  initiatedBy: 'client' | 'expert';
}

export interface ClientAiReportsPage {
  data: ClientAiReportListItem[];
  meta: { total: number; limit: number; offset: number };
}

export const getClientAiReports = async (
  clientId: string,
  limit: number,
  offset: number,
): Promise<ClientAiReportsPage> => {
  const response = await api.get(`/client/${clientId}/reports/ai`, { params: { limit, offset } });
  return response.data;
};

export const getClientAiReport = async (
  clientId: string,
  reportId: number,
): Promise<ClientAiReportDetail> => {
  const response = await api.get(`/client/${clientId}/reports/ai/${reportId}`);
  return unwrapApiResponse<ClientAiReportDetail>(response.data);
};

export const getClientActivityAiAnalysis = async (
  clientId: string,
  from: string,
  to: string,
  sections?: string[],
  task: { prompt?: string; promptId?: number } = {},
): Promise<AiAnalysisResult> => {
  const response = await api.post(`/client/${clientId}/reports/activity/ai-analysis`, {
    from,
    to,
    ...(sections && sections.length > 0 ? { sections } : {}),
    // Бібліотечний промпт експерта - за id (бекенд перевіряє власника), власний текст - як prompt.
    ...(task.promptId ? { promptId: task.promptId } : task.prompt ? { prompt: task.prompt } : {}),
  });
  const data = unwrapApiResponse<{ html: string; fromCache?: boolean }>(response.data);
  return { html: data.html, fromCache: data.fromCache ?? false };
};
